import React, { useEffect, useState } from 'react';
import { api } from '../hooks/useApi';
import Icon from './Icon';

/**
 * Panneau "Stock matieres — recette {produit}"
 * Affiche sur la page de creation de batch pour prevenir les ruptures.
 *
 * Props :
 * - produit         : 'porc' | 'saumon' | 'cotes_levees' | 'brisket' | 'saucisses' | 'sauce' | 'tallow'
 * - poidsCruKg      : (optionnel) poids cru saisi par l'utilisateur — recalcule les quantites requises
 * - multiplicateur  : (optionnel) multiplicateur recette (sauce)
 */
export default function RecetteStockPanel({ produit, poidsCruKg, multiplicateur }) {
  const [items, setItems]     = useState([]);
  const [loading, setLoading] = useState(false);
  const [erreur, setErreur]   = useState('');

  useEffect(() => {
    if (!produit) return;
    setLoading(true);
    setErreur('');
    const params = new URLSearchParams();
    if (poidsCruKg && parseFloat(poidsCruKg) > 0) params.append('poids_cru_kg', poidsCruKg);
    if (multiplicateur && parseFloat(multiplicateur) > 0) params.append('multiplicateur', multiplicateur);

    api.get(`/inventaire/recette-stock/${produit}?${params.toString()}`)
      .then(d => setItems(d.items || []))
      .catch(e => {
        setErreur(e.response?.data?.error || 'Impossible de charger le stock');
        setItems([]);
      })
      .finally(() => setLoading(false));
  }, [produit, poidsCruKg, multiplicateur]);

  // ── Etats ──

  if (loading && items.length === 0) {
    return (
      <div className="card" style={{ marginBottom: 'var(--space-4)' }}>
        <div className="skeleton" style={{ height: 13, width: 180, marginBottom: 'var(--space-3)' }} />
        <div className="skeleton" style={{ height: 11, width: '100%', marginBottom: 6 }} />
        <div className="skeleton" style={{ height: 11, width: '85%' }} />
      </div>
    );
  }

  if (erreur) {
    return (
      <div className="card" style={{ marginBottom: 'var(--space-4)' }}>
        <div className="error-inline">
          <Icon name="erreur" size={15} />
          {erreur}
        </div>
      </div>
    );
  }

  if (!items.length) return null;

  // ── Helpers badge ──

  function badgeStatut(s) {
    if (s === 'negatif') return <span className="badge badge--danger"><Icon name="erreur" size={11} /> Insuffisant</span>;
    if (s === 'bas')     return <span className="badge badge--warning"><Icon name="attention" size={11} /> Juste</span>;
    if (s === 'ok')      return <span className="badge badge--success"><Icon name="check" size={11} /> OK</span>;
    if (s === 'na')      return <span className="badge">--</span>;
    return                      <span className="badge muted">A la production</span>;
  }

  const nbInsuf = items.filter(i => i.statut === 'negatif').length;

  return (
    <div
      className="card"
      style={{
        marginBottom: 'var(--space-4)',
        borderColor: nbInsuf > 0 ? 'var(--danger)' : undefined,
      }}
    >
      {/* En-tete */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--space-4)' }}>
        <h4 style={{
          fontFamily: "'Cinzel', serif",
          fontSize: 12,
          fontWeight: 600,
          color: 'var(--text-soft)',
          textTransform: 'uppercase',
          letterSpacing: '0.08em',
          margin: 0,
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--space-2)',
        }}>
          <Icon name="liste" size={14} />
          Stock matieres — recette
        </h4>
        {nbInsuf > 0 && (
          <span className="badge badge--danger">
            <Icon name="attention" size={11} />
            {nbInsuf} en rupture
          </span>
        )}
      </div>

      {/* Tableau */}
      <table className="esc-table">
        <thead>
          <tr>
            <th>Ingredient</th>
            <th style={{ textAlign: 'right' }}>Stock actuel</th>
            <th style={{ textAlign: 'right' }}>Requis</th>
            <th style={{ textAlign: 'center' }}>Statut</th>
          </tr>
        </thead>
        <tbody>
          {items.map(it => (
            <tr key={it.matiere_id}>
              <td>{it.nom}</td>
              <td style={{ textAlign: 'right', fontFeatureSettings: "'tnum' 1" }}>
                {it.stock_actuel} {it.unite}
              </td>
              <td style={{ textAlign: 'right', fontFeatureSettings: "'tnum' 1" }}>
                {it.calculable
                  ? `${it.quantite_requise} ${it.unite}`
                  : <span className="muted">a la production</span>}
              </td>
              <td style={{ textAlign: 'center' }}>
                {badgeStatut(it.statut)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <p className="muted" style={{ fontSize: 11, marginTop: 'var(--space-3)', marginBottom: 0 }}>
        Les quantites sont recalculees a mesure que tu remplis le formulaire.
        Les ingredients « a la production » dependent du nombre d'unites finales.
      </p>
    </div>
  );
}
